import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  ChevronRight,
  Clock,
  Loader2,
  MapPin,
  Play,
  Plus,
  Trash2,
  Users,
  Video,
} from 'lucide-react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Switch } from '../../ui/switch';
import { Textarea } from '../../ui/textarea';
import CalendarRolloverSection from './site-settings/CalendarRolloverSection';
import CallCalendarSection from './site-settings/CallCalendarSection';
import UpcomingEventsSection from './site-settings/UpcomingEventsSection';
import SidebarPhotosSection from './site-settings/sidebar-photos/SidebarPhotosSection';
import {
  getLaunchReadinessGaps,
  getUpcomingEventIssues,
  syncFounding40Events,
} from './site-settings/upcomingEvents';

const SITE_SETTINGS_CARD_CLASS = 'border border-gray-200 bg-white dark:border-[#2a2a32] dark:bg-[#1e1e24]';
const SITE_SETTINGS_SECTION_TITLE_CLASS = 'text-lg font-semibold text-gray-900 dark:text-white';
const SITE_SETTINGS_BODY_TEXT_CLASS = 'text-sm text-gray-600 dark:text-gray-400';
const SITE_SETTINGS_LABEL_CLASS = 'text-sm text-gray-700 dark:text-gray-400';
const SITE_SETTINGS_SUBTLE_TEXT_CLASS = 'text-xs text-gray-500 dark:text-gray-500';
const SITE_SETTINGS_INPUT_CLASS = 'border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-white dark:placeholder:text-gray-500';
const SITE_SETTINGS_DATETIME_INPUT_CLASS = `${SITE_SETTINGS_INPUT_CLASS} site-settings-datetime-input`;
const SITE_SETTINGS_DATE_INPUT_CLASS = `${SITE_SETTINGS_INPUT_CLASS} site-settings-date-input`;
const SITE_SETTINGS_SELECT_CLASS = 'flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-aqua dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-white';
const SITE_SETTINGS_PANEL_CLASS = 'rounded-xl border border-gray-200 bg-gray-50 dark:border-[#2a2a32] dark:bg-[#16161a]';
const SITE_SETTINGS_PANEL_SOFT_CLASS = 'rounded-xl border border-gray-200 bg-gray-50/80 dark:border-white/5 dark:bg-black/20';
const SITE_SETTINGS_PANEL_DEEP_CLASS = 'rounded-xl border border-gray-200 bg-gray-50 dark:border-[#2a2a32] dark:bg-[#121216]';
const SITE_SETTINGS_OUTLINE_BUTTON_CLASS = 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-[#2a2a32] dark:bg-transparent dark:text-white dark:hover:bg-[#16161a]';
const SITE_SETTINGS_NEUTRAL_BADGE_CLASS = 'rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-200';
const SITE_SETTINGS_MUTED_PILL_CLASS = 'rounded-lg bg-white px-3 py-2 text-sm text-gray-700 dark:bg-white/[0.03] dark:text-gray-300';
const SITE_SETTINGS_MUTED_PILL_TIGHT_CLASS = 'rounded-lg bg-white px-2.5 py-2 text-[11px] leading-4 text-gray-700 dark:bg-white/[0.03] dark:text-gray-300';
const SITE_SETTINGS_PHOTO_OVERLAY_PILL_CLASS = 'rounded-full border border-white/25 bg-slate-950/82 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-white shadow-sm backdrop-blur-sm';
const SITE_SETTINGS_STATUS_PILL_CLASS = 'rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em]';
const SITE_SETTINGS_DISCLOSURE_ICON_CLASS = 'mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-400/20 dark:bg-sky-400/10 dark:text-sky-300';
const SITE_SETTINGS_PRIORITY_BADGE_CLASS = 'inline-flex h-7 min-w-7 items-center justify-center rounded-full bg-emerald-700 px-2 text-xs font-semibold text-white dark:bg-emerald-500';

const DEFAULT_LAUNCH_CRITICAL_PATH_SETTINGS = {
  enabled: true,
  label: 'Current Focus',
  helper_text: 'Keep the few highest-priority items in view above the daily queue.',
  items: [],
};
const FOUNDING_40_SERIES_DATES = [
  '2026-05-17',
  '2026-05-31',
  '2026-06-07',
  '2026-06-14',
  '2026-06-20',
];

const CRITICAL_PATH_TONE_OPTIONS = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'attention', label: 'Attention' },
  { value: 'next', label: 'Next' },
];

const CRITICAL_PATH_DESTINATION_OPTIONS = [
  { value: 'guide-partners:guestguides', label: 'Guide Partners -> Outreach' },
  { value: 'reach-dads:members', label: 'Prospects -> Prospects' },
  { value: 'listen-learn:feedback', label: 'Feedback & Insight -> Feedback' },
  { value: 'support-dads:recordings', label: 'Dad Partners -> Recordings' },
  { value: 'system-health:sitesettings', label: 'Admin Systems -> Site Settings' },
  { value: 'steward-the-work:analytics', label: 'Operations -> Analytics' },
];

const AUTO_CRITICAL_PATH_ITEMS = [
  {
    id: 'founding-40',
    label: 'Founding 40 spots need to be filled',
    description: 'Keep the remaining Founding 40 seats first until the group is full.',
    defaultTone: 'urgent',
    room_id: 'reach-dads',
    tab: 'members',
  },
  {
    id: 'guide-partners-ready',
    label: 'Guide Partners ready to schedule',
    description: 'Show the scheduling-ready Guide Partners who are waiting on Kevin.',
    defaultTone: 'attention',
    room_id: 'guide-partners',
    tab: 'guestguides',
  },
  {
    id: 'guide-partners-overdue',
    label: 'Guide Partners overdue on onboarding',
    description: 'Keep overdue onboarding follow-up visible when handoffs begin to stall.',
    defaultTone: 'attention',
    room_id: 'guide-partners',
    tab: 'guestguides',
  },
];

const createManualCriticalPathItem = (sortOrder = 999) => ({
  id: `manual_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  source: 'manual',
  enabled: true,
  sort_order: sortOrder,
  tone: 'next',
  title_override: '',
  detail_override: '',
  action_label_override: '',
  room_id: 'guide-partners',
  tab: 'guestguides',
});

const SITE_SETTINGS_SHARED_STYLES = {
  card: SITE_SETTINGS_CARD_CLASS,
  sectionTitle: SITE_SETTINGS_SECTION_TITLE_CLASS,
  bodyText: SITE_SETTINGS_BODY_TEXT_CLASS,
  label: SITE_SETTINGS_LABEL_CLASS,
  subtleText: SITE_SETTINGS_SUBTLE_TEXT_CLASS,
  input: SITE_SETTINGS_INPUT_CLASS,
  dateInput: SITE_SETTINGS_DATE_INPUT_CLASS,
  select: SITE_SETTINGS_SELECT_CLASS,
  outlineButton: SITE_SETTINGS_OUTLINE_BUTTON_CLASS,
  neutralBadge: SITE_SETTINGS_NEUTRAL_BADGE_CLASS,
  mutedPill: SITE_SETTINGS_MUTED_PILL_CLASS,
  mutedPillTight: SITE_SETTINGS_MUTED_PILL_TIGHT_CLASS,
  photoOverlayPill: SITE_SETTINGS_PHOTO_OVERLAY_PILL_CLASS,
  statusPill: SITE_SETTINGS_STATUS_PILL_CLASS,
  disclosureIcon: SITE_SETTINGS_DISCLOSURE_ICON_CLASS,
  panel: SITE_SETTINGS_PANEL_CLASS,
  panelSoft: SITE_SETTINGS_PANEL_SOFT_CLASS,
  panelDeep: SITE_SETTINGS_PANEL_DEEP_CLASS,
};

const SiteSettingsTab = ({
  settings,
  setSettings,
  saving,
  loading,
  openRequestKey,
  handleSave,
  adminFetch,
  statusItems = [],
  targetAnchor = null,
  targetRecordId = null,
  targetRequestKey = null,
}) => {
  const [openSections, setOpenSections] = useState(['core-launch', 'launch-critical']);
  const [openCriticalPathItems, setOpenCriticalPathItems] = useState([]);
  const [highlightedSectionKey, setHighlightedSectionKey] = useState(null);
  const siteSettingsTopRef = useRef(null);
  const launchCriticalRef = useRef(null);
  const zoomSettingsRef = useRef(null);
  const introVideoRef = useRef(null);
  const founding40Ref = useRef(null);
  const sidebarPhotosRef = useRef(null);
  const saveLaunchCheckRef = useRef(null);
  const allSiteEvents = useMemo(
    () => (Array.isArray(settings.upcoming_events) ? settings.upcoming_events : []),
    [settings.upcoming_events]
  );
  const saveBlockers = allSiteEvents.flatMap((event, index) => (
    getUpcomingEventIssues(event).map((issue) => `Event ${index + 1}: ${issue}`)
  ));
  const founding40SeriesDates = Array.isArray(settings.founding_40_series_dates) && settings.founding_40_series_dates.length > 0
    ? settings.founding_40_series_dates
    : [...FOUNDING_40_SERIES_DATES];
  const launchReadinessGaps = getLaunchReadinessGaps(settings, allSiteEvents);
  const launchCriticalPath = {
    ...DEFAULT_LAUNCH_CRITICAL_PATH_SETTINGS,
    ...(settings.launch_critical_path || {}),
    items: Array.isArray(settings.launch_critical_path?.items) ? settings.launch_critical_path.items : [],
  };
  const autoCriticalPathItems = AUTO_CRITICAL_PATH_ITEMS.map((item, index) => {
    const saved = launchCriticalPath.items.find((candidate) => candidate.id === item.id) || {};
    return {
      ...item,
      source: 'auto',
      enabled: true,
      sort_order: Number.isFinite(saved.sort_order) ? saved.sort_order : index + 1,
      tone: saved.tone || item.defaultTone,
      title_override: saved.title_override || '',
      detail_override: saved.detail_override || '',
      action_label_override: saved.action_label_override || '',
      room_id: saved.room_id || item.room_id,
      tab: saved.tab || item.tab,
    };
  }).sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
  const manualCriticalPathItems = launchCriticalPath.items
    .filter((item) => (item.source || 'manual') === 'manual')
    .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));

  const buildSettingsWithSyncedFounding40Events = (settingsPatch = {}) => {
    const nextSettings = {
      ...settings,
      ...settingsPatch,
    };

    return {
      ...nextSettings,
      upcoming_events: syncFounding40Events({
        events: nextSettings.upcoming_events,
        founding40SeriesDates: nextSettings.founding_40_series_dates,
        founding40Time: nextSettings.founding_40_time,
      }),
    };
  };

  useEffect(() => {
    const syncedEvents = syncFounding40Events({
      events: settings.upcoming_events,
      founding40SeriesDates: settings.founding_40_series_dates,
      founding40Time: settings.founding_40_time,
    });

    if (syncedEvents === settings.upcoming_events) return;

    setSettings((current) => ({
      ...current,
      upcoming_events: syncedEvents,
    }));
  }, [
    setSettings,
    settings.founding_40_series_dates,
    settings.founding_40_time,
    settings.upcoming_events,
  ]);

  const updateLaunchCriticalPath = (patch) => {
    setSettings({
      ...settings,
      launch_critical_path: {
        ...launchCriticalPath,
        ...patch,
      },
    });
  };

  const upsertLaunchCriticalPathItem = (itemId, patch, source = 'auto') => {
    const existing = launchCriticalPath.items.find((item) => item.id === itemId);
    const defaultItem = source === 'auto'
      ? AUTO_CRITICAL_PATH_ITEMS.find((item) => item.id === itemId)
      : null;
    const nextItem = {
      id: itemId,
      source,
      enabled: true,
      sort_order: launchCriticalPath.items.length + 1,
      tone: defaultItem?.defaultTone || 'next',
      room_id: defaultItem?.room_id || '',
      tab: defaultItem?.tab || '',
      title_override: '',
      detail_override: '',
      action_label_override: '',
      ...existing,
      ...patch,
    };

    updateLaunchCriticalPath({
      items: existing
        ? launchCriticalPath.items.map((item) => (item.id === itemId ? nextItem : item))
        : [...launchCriticalPath.items, nextItem],
    });
  };

  const removeLaunchCriticalPathItem = (itemId) => {
    updateLaunchCriticalPath({
      items: launchCriticalPath.items.filter((item) => item.id !== itemId),
    });
  };

  const addManualCriticalPathItem = () => {
    const nextSortOrder = launchCriticalPath.items.reduce(
      (max, item) => Math.max(max, Number(item.sort_order) || 0),
      0
    ) + 1;
    const nextItem = createManualCriticalPathItem(nextSortOrder);
    updateLaunchCriticalPath({
      items: [...launchCriticalPath.items, nextItem],
    });
    setOpenCriticalPathItems((current) => (
      current.includes(nextItem.id) ? current : [...current, nextItem.id]
    ));
  };

  const updateFounding40SeriesDate = (index, value) => {
    setSettings(buildSettingsWithSyncedFounding40Events({
      founding_40_series_dates: founding40SeriesDates.map((date, candidateIndex) => (
        candidateIndex === index ? value : date
      )),
      founding_40_start_date: index === 0 ? value : settings.founding_40_start_date,
    }));
  };

  const updateFounding40StartDate = (value) => {
    const nextSeriesDates = founding40SeriesDates.map((date, index) => (index === 0 ? value : date));
    setSettings(buildSettingsWithSyncedFounding40Events({
      founding_40_start_date: value,
      founding_40_series_dates: nextSeriesDates,
    }));
  };

  const updateFounding40Time = (value) => {
    setSettings(buildSettingsWithSyncedFounding40Events({
      founding_40_time: value,
    }));
  };

  const getDestinationValue = (item) => `${item.room_id || ''}:${item.tab || ''}`;

  const updateLaunchCriticalPathDestination = (itemId, value, source = 'manual') => {
    const [room_id = '', tab = ''] = String(value || '').split(':');
    upsertLaunchCriticalPathItem(itemId, { room_id, tab }, source);
  };

  const toggleCriticalPathItem = (itemId) => {
    setOpenCriticalPathItems((current) => (
      current.includes(itemId)
        ? current.filter((candidate) => candidate !== itemId)
        : [...current, itemId]
    ));
  };
  const enabledCriticalPathCount = autoCriticalPathItems.length + manualCriticalPathItems.filter((item) => item.enabled !== false).length;
  const coreLaunchReadyCount = [
    !!String(settings.zoom_link || '').trim(),
    !!String(settings.founding_40_start_date || '').trim(),
    !!String(settings.founding_40_time || '').trim(),
  ].filter(Boolean).length;

  useEffect(() => {
    if (loading || !targetAnchor || !targetRequestKey) return;

    const normalizedAnchor = String(targetAnchor).trim().toLowerCase();
    const refMap = {
      'launch-critical': launchCriticalRef,
      'zoom-settings': zoomSettingsRef,
      'intro-video': introVideoRef,
      'founding-40-settings': founding40Ref,
      'sidebar-photos': sidebarPhotosRef,
      'find-sidebar-photos': sidebarPhotosRef,
      'save-and-launch-check': saveLaunchCheckRef,
    };
    const targetRef = refMap[normalizedAnchor] || siteSettingsTopRef;

    if (normalizedAnchor === 'launch-critical') {
      setOpenSections((current) => (
        current.includes('launch-critical') ? current : [...current, 'launch-critical']
      ));
    }
    if (normalizedAnchor === 'upcoming-events') {
      setOpenSections((current) => (
        current.includes('upcoming-events') ? current : [...current, 'upcoming-events']
      ));
    }
    if (normalizedAnchor === 'sidebar-photos' || normalizedAnchor === 'find-sidebar-photos') {
      setOpenSections((current) => {
        const next = [...current];
        if (!next.includes('sidebar-photos')) next.push('sidebar-photos');
        if (normalizedAnchor === 'find-sidebar-photos' && !next.includes('find-sidebar-photos')) next.push('find-sidebar-photos');
        return next;
      });
    }

    const frame = window.requestAnimationFrame(() => {
      targetRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
    setHighlightedSectionKey(normalizedAnchor);
    const timeout = window.setTimeout(() => setHighlightedSectionKey(null), 1800);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
    };
  }, [loading, targetAnchor, targetRequestKey]);

  const highlightedCardClass = (anchorKey) => (
    highlightedSectionKey === anchorKey ? 'ring-2 ring-aqua/40' : ''
  );

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 text-aqua animate-spin" />
      </div>
    );
  }

  return (
    <div ref={siteSettingsTopRef} className="flex flex-col gap-6 scroll-mt-32">
      <Card className={`${SITE_SETTINGS_CARD_CLASS} p-6`}>
        <div className="max-w-3xl">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-aqua">
            Site Settings
          </p>
          <h2 className={`${SITE_SETTINGS_SECTION_TITLE_CLASS} mt-2`}>
            Keep launch-critical settings and core admin controls in one place
          </h2>
          <p className={`${SITE_SETTINGS_BODY_TEXT_CLASS} mt-3`}>
            Use this tool to maintain the live settings that shape launch readiness, public-facing event details,
            and the core admin controls that keep Dad Partners operational.
          </p>
        </div>
      </Card>

      <Card className={`${SITE_SETTINGS_CARD_CLASS} p-6`}>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-aqua">
            What must be working
          </p>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {statusItems.map((item) => (
            <div key={item.key} className="rounded-xl border border-gray-200 bg-gray-50/80 px-3 py-3 dark:border-white/10 dark:bg-white/[0.03]">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{item.label}</p>
                <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                  item.tone === 'ready'
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300'
                    : 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300'
                }`}>
                  {item.status}
                </span>
              </div>
              <p className="mt-2 text-xs leading-5 text-gray-600 dark:text-gray-400">{item.detail}</p>
            </div>
          ))}
        </div>
      </Card>

      <Card ref={launchCriticalRef} className={`${SITE_SETTINGS_CARD_CLASS} ${highlightedCardClass('launch-critical')} p-6`}>
        <button
          type="button"
          onClick={() => setOpenSections((current) => (
            current.includes('launch-critical')
              ? current.filter((item) => item !== 'launch-critical')
              : [...current, 'launch-critical']
          ))}
          className="flex w-full items-start gap-3 text-left"
        >
          <span className={SITE_SETTINGS_DISCLOSURE_ICON_CLASS}>
            <ChevronRight className={`h-4 w-4 transition-transform ${openSections.includes('launch-critical') ? 'rotate-90' : ''}`} />
          </span>
          <div className="min-w-0 flex-1 max-w-3xl">
            <h3 className={SITE_SETTINGS_SECTION_TITLE_CLASS}>
              Current Focus
            </h3>
            <p className={`${SITE_SETTINGS_BODY_TEXT_CLASS} mt-2`}>
              This powers the Command Center banner that sits above the daily queue when you want a small set of priorities to stay unmistakably visible.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className={SITE_SETTINGS_NEUTRAL_BADGE_CLASS}>{autoCriticalPathItems.length} ranked priorities</span>
              <span className={SITE_SETTINGS_NEUTRAL_BADGE_CLASS}>{manualCriticalPathItems.length} manual</span>
            </div>
          </div>
        </button>

        {openSections.includes('launch-critical') && (
        <>
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div>
            <label className={`${SITE_SETTINGS_LABEL_CLASS} mb-1 block`}>Banner label</label>
            <Input
              value={launchCriticalPath.label}
              onChange={(e) => updateLaunchCriticalPath({ label: e.target.value })}
              className={SITE_SETTINGS_INPUT_CLASS}
              placeholder="Current Focus"
            />
          </div>
          <div>
            <label className={`${SITE_SETTINGS_LABEL_CLASS} mb-1 block`}>Helper line</label>
            <Input
              value={launchCriticalPath.helper_text}
              onChange={(e) => updateLaunchCriticalPath({ helper_text: e.target.value })}
              className={SITE_SETTINGS_INPUT_CLASS}
              placeholder="Keep the few highest-priority items in view."
            />
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-amber-200/70 bg-amber-50/70 p-4 dark:border-amber-400/15 dark:bg-amber-500/5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-amber-700 dark:text-amber-300">
                Current Focus items
              </p>
              <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                These start from live operating signals, but this is the set that should stay unmistakably important.
              </p>
            </div>
            <div className={SITE_SETTINGS_NEUTRAL_BADGE_CLASS}>{autoCriticalPathItems.length} items</div>
          </div>

          <div className="mt-4 space-y-4">
            {autoCriticalPathItems.map((item) => (
              <div key={item.id} className={`${SITE_SETTINGS_PANEL_CLASS} p-4`}>
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <button
                    type="button"
                    onClick={() => toggleCriticalPathItem(item.id)}
                    className="flex min-w-0 flex-1 items-start gap-3 text-left"
                  >
                    <span className={SITE_SETTINGS_PRIORITY_BADGE_CLASS}>
                      {item.sort_order ?? 999}
                    </span>
                    <span className={SITE_SETTINGS_DISCLOSURE_ICON_CLASS}>
                      <ChevronRight className={`h-4 w-4 transition-transform ${openCriticalPathItems.includes(item.id) ? 'rotate-90' : ''}`} />
                    </span>
                    <div className="max-w-2xl">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{item.label}</p>
                      <p className={`${SITE_SETTINGS_BODY_TEXT_CLASS} mt-1`}>{item.description}</p>
                    </div>
                  </button>
                </div>

                {openCriticalPathItems.includes(item.id) && (
                <div className="mt-4 grid gap-4 md:grid-cols-4">
                  <div>
                    <label className={`${SITE_SETTINGS_LABEL_CLASS} mb-1 block`}>Priority</label>
                    <Input
                      type="number"
                      value={item.sort_order}
                      onChange={(e) => upsertLaunchCriticalPathItem(item.id, { sort_order: parseInt(e.target.value, 10) || 999 }, 'auto')}
                      className={SITE_SETTINGS_INPUT_CLASS}
                    />
                  </div>
                  <div>
                    <label className={`${SITE_SETTINGS_LABEL_CLASS} mb-1 block`}>Tone</label>
                    <select
                      value={item.tone}
                      onChange={(e) => upsertLaunchCriticalPathItem(item.id, { tone: e.target.value }, 'auto')}
                      className={SITE_SETTINGS_SELECT_CLASS}
                    >
                      {CRITICAL_PATH_TONE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className={`${SITE_SETTINGS_LABEL_CLASS} mb-1 block`}>Destination</label>
                    <select
                      value={getDestinationValue(item)}
                      onChange={(e) => updateLaunchCriticalPathDestination(item.id, e.target.value, 'auto')}
                      className={SITE_SETTINGS_SELECT_CLASS}
                    >
                      {CRITICAL_PATH_DESTINATION_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className={`${SITE_SETTINGS_LABEL_CLASS} mb-1 block`}>Title override</label>
                    <Input
                      value={item.title_override}
                      onChange={(e) => upsertLaunchCriticalPathItem(item.id, { title_override: e.target.value }, 'auto')}
                      className={SITE_SETTINGS_INPUT_CLASS}
                      placeholder="Leave blank to use the system wording"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className={`${SITE_SETTINGS_LABEL_CLASS} mb-1 block`}>Action label override</label>
                    <Input
                      value={item.action_label_override}
                      onChange={(e) => upsertLaunchCriticalPathItem(item.id, { action_label_override: e.target.value }, 'auto')}
                      className={SITE_SETTINGS_INPUT_CLASS}
                      placeholder="Optional button text"
                    />
                  </div>
                  <div className="md:col-span-4">
                    <label className={`${SITE_SETTINGS_LABEL_CLASS} mb-1 block`}>Detail override</label>
                    <Textarea
                      value={item.detail_override}
                      onChange={(e) => upsertLaunchCriticalPathItem(item.id, { detail_override: e.target.value }, 'auto')}
                      className={`${SITE_SETTINGS_INPUT_CLASS} min-h-[84px]`}
                      placeholder="Leave blank to use the system detail"
                    />
                  </div>
                </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-emerald-200/70 bg-emerald-50/60 p-4 dark:border-emerald-400/15 dark:bg-emerald-500/5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-700 dark:text-emerald-300">
                Manual critical-path items
              </p>
              <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                Add the founder-priority work the system cannot infer yet, then order it alongside the auto items.
              </p>
            </div>
            <Button type="button" onClick={addManualCriticalPathItem} className="bg-aqua text-charcoal hover:bg-aqua-dark">
              <Plus className="mr-2 h-4 w-4" />
              Add manual item
            </Button>
          </div>

          <div className="mt-4 space-y-4">
            {manualCriticalPathItems.length === 0 ? (
              <div className={`${SITE_SETTINGS_PANEL_CLASS} p-4`}>
                <p className={SITE_SETTINGS_BODY_TEXT_CLASS}>
                  No manual critical-path items yet. Add one when the real launch blocker lives in your head instead of the data.
                </p>
              </div>
            ) : manualCriticalPathItems.map((item) => (
              <div key={item.id} className="rounded-xl border border-emerald-200/70 bg-white/90 p-4 dark:border-emerald-400/10 dark:bg-[#14181a]">
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    onClick={() => toggleCriticalPathItem(item.id)}
                    className="flex min-w-0 flex-1 items-start gap-3 text-left"
                  >
                    <span className={SITE_SETTINGS_PRIORITY_BADGE_CLASS}>
                      {item.sort_order ?? 999}
                    </span>
                    <span className={SITE_SETTINGS_DISCLOSURE_ICON_CLASS}>
                      <ChevronRight className={`h-4 w-4 transition-transform ${openCriticalPathItems.includes(item.id) ? 'rotate-90' : ''}`} />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {item.title_override?.trim() || 'Untitled manual item'}
                      </p>
                      <p className={`${SITE_SETTINGS_SUBTLE_TEXT_CLASS} mt-1`}>
                        Manual item • priority {item.sort_order ?? 999}
                      </p>
                    </div>
                  </button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => removeLaunchCriticalPathItem(item.id)}
                    className={SITE_SETTINGS_OUTLINE_BUTTON_CLASS}
                    size="icon"
                    aria-label={`Delete ${item.title_override?.trim() || 'manual launch item'}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                {openCriticalPathItems.includes(item.id) && (
                <div className="mt-4 grid gap-4 md:grid-cols-4">
                  <div>
                    <label className={`${SITE_SETTINGS_LABEL_CLASS} mb-1 block`}>Priority</label>
                    <Input
                      type="number"
                      value={item.sort_order ?? 999}
                      onChange={(e) => upsertLaunchCriticalPathItem(item.id, { sort_order: parseInt(e.target.value, 10) || 999 }, 'manual')}
                      className={SITE_SETTINGS_INPUT_CLASS}
                    />
                  </div>
                  <div>
                    <label className={`${SITE_SETTINGS_LABEL_CLASS} mb-1 block`}>Tone</label>
                    <select
                      value={item.tone || 'next'}
                      onChange={(e) => upsertLaunchCriticalPathItem(item.id, { tone: e.target.value }, 'manual')}
                      className={SITE_SETTINGS_SELECT_CLASS}
                    >
                      {CRITICAL_PATH_TONE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className={`${SITE_SETTINGS_LABEL_CLASS} mb-1 block`}>Destination</label>
                    <select
                      value={getDestinationValue(item)}
                      onChange={(e) => updateLaunchCriticalPathDestination(item.id, e.target.value, 'manual')}
                      className={SITE_SETTINGS_SELECT_CLASS}
                    >
                      {CRITICAL_PATH_DESTINATION_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className={`${SITE_SETTINGS_LABEL_CLASS} mb-1 block`}>Title</label>
                    <Input
                      value={item.title_override || ''}
                      onChange={(e) => upsertLaunchCriticalPathItem(item.id, { title_override: e.target.value }, 'manual')}
                      className={SITE_SETTINGS_INPUT_CLASS}
                      placeholder="Fill the final Founding 40 seats"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className={`${SITE_SETTINGS_LABEL_CLASS} mb-1 block`}>Action label</label>
                    <Input
                      value={item.action_label_override || ''}
                      onChange={(e) => upsertLaunchCriticalPathItem(item.id, { action_label_override: e.target.value }, 'manual')}
                      className={SITE_SETTINGS_INPUT_CLASS}
                      placeholder="Open Guide Partners"
                    />
                  </div>
                  <div className="md:col-span-4">
                    <label className={`${SITE_SETTINGS_LABEL_CLASS} mb-1 block`}>Detail</label>
                    <Textarea
                      value={item.detail_override || ''}
                      onChange={(e) => upsertLaunchCriticalPathItem(item.id, { detail_override: e.target.value }, 'manual')}
                      className={`${SITE_SETTINGS_INPUT_CLASS} min-h-[96px]`}
                      placeholder="Add the launch-specific context you want to see at the top of Command Center."
                    />
                  </div>
                </div>
                )}
              </div>
            ))}
          </div>
        </div>
        </>
        )}
      </Card>

      <CalendarRolloverSection adminFetch={adminFetch} styles={SITE_SETTINGS_SHARED_STYLES} />

      <CallCalendarSection adminFetch={adminFetch} styles={SITE_SETTINGS_SHARED_STYLES} />

      <UpcomingEventsSection
        settings={settings}
        setSettings={setSettings}
        openSections={openSections}
        setOpenSections={setOpenSections}
        styles={SITE_SETTINGS_SHARED_STYLES}
        targetEventId={String(targetAnchor).trim().toLowerCase() === 'upcoming-events' ? targetRecordId || null : null}
        targetRequestKey={String(targetAnchor).trim().toLowerCase() === 'upcoming-events' ? targetRequestKey : null}
      />

      <Card ref={zoomSettingsRef} className={`${SITE_SETTINGS_CARD_CLASS} ${highlightedCardClass('zoom-settings')} p-6`}>
        <h3 className={`${SITE_SETTINGS_SECTION_TITLE_CLASS} mb-4 flex items-center gap-2`}>
          <Video className="w-5 h-5 text-aqua" />
          Zoom Meeting Settings
        </h3>
        <p className={`${SITE_SETTINGS_BODY_TEXT_CLASS} mb-4`}>
          Set the Zoom link that will be used for all scheduled calls.
        </p>
        <div className="space-y-4">
          <div>
            <label className={`${SITE_SETTINGS_LABEL_CLASS} mb-1 block`}>Zoom Meeting Link</label>
            <Input
              value={settings.zoom_link}
              onChange={(e) => setSettings({ ...settings, zoom_link: e.target.value })}
              className={SITE_SETTINGS_INPUT_CLASS}
              placeholder="https://zoom.us/j/..."
            />
          </div>
        </div>
      </Card>

      <Card ref={introVideoRef} className={`${SITE_SETTINGS_CARD_CLASS} ${highlightedCardClass('intro-video')} p-6`}>
        <h3 className={`${SITE_SETTINGS_SECTION_TITLE_CLASS} mb-4 flex items-center gap-2`}>
          <Play className="w-5 h-5 text-aqua" />
          Introduction Video
        </h3>
        <p className={`${SITE_SETTINGS_BODY_TEXT_CLASS} mb-4`}>
          Set the welcome video shown on the home page "Watch Introduction" section.
        </p>
        <div className="space-y-4">
          <div>
            <label className={`${SITE_SETTINGS_LABEL_CLASS} mb-1 block`}>Video URL (YouTube, Vimeo, or direct link)</label>
            <Input
              value={settings.intro_video_url}
              onChange={(e) => setSettings({ ...settings, intro_video_url: e.target.value })}
              className={SITE_SETTINGS_INPUT_CLASS}
              placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
            />
          </div>
          {settings.intro_video_url && (
            <div className={`${SITE_SETTINGS_PANEL_CLASS} p-4`}>
              <p className={`${SITE_SETTINGS_BODY_TEXT_CLASS} mb-2`}>Preview:</p>
              <a
                href={settings.intro_video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-aqua hover:underline text-sm"
              >
                {settings.intro_video_url}
              </a>
            </div>
          )}
        </div>
      </Card>

      <Card ref={founding40Ref} className={`${SITE_SETTINGS_CARD_CLASS} ${highlightedCardClass('founding-40-settings')} p-6`}>
        <h3 className={`${SITE_SETTINGS_SECTION_TITLE_CLASS} mb-4 flex items-center gap-2`}>
          <Users className="w-5 h-5 text-aqua" />
          Weekly Call Settings
        </h3>
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className={`${SITE_SETTINGS_LABEL_CLASS} mb-1 block`}>First call date</label>
            <Input
              type="date"
              value={settings.founding_40_start_date}
              onChange={(e) => updateFounding40StartDate(e.target.value)}
              className={SITE_SETTINGS_DATE_INPUT_CLASS}
            />
          </div>
          <div>
            <label className={`${SITE_SETTINGS_LABEL_CLASS} mb-1 block`}>Time (Sundays)</label>
            <Input
              value={settings.founding_40_time}
              onChange={(e) => updateFounding40Time(e.target.value)}
              className={SITE_SETTINGS_INPUT_CLASS}
              placeholder="4:00 PM ET"
            />
          </div>
          <div>
            <label className={`${SITE_SETTINGS_LABEL_CLASS} mb-1 block`}>Public call panel</label>
            <select
              value={settings.founding_40_public_mode || 'join'}
              onChange={(e) => setSettings({ ...settings, founding_40_public_mode: e.target.value })}
              className={SITE_SETTINGS_SELECT_CLASS}
            >
              <option value="webinar">Webinar panel</option>
              <option value="join">Join panel</option>
            </select>
          </div>
        </div>
        <div className="mt-4">
          <label className={`${SITE_SETTINGS_LABEL_CLASS} mb-2 block`}>Call dates</label>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {founding40SeriesDates.map((date, index) => (
              <Input
                key={`founding-40-series-${index}`}
                type="date"
                value={date}
                onChange={(e) => updateFounding40SeriesDate(index, e.target.value)}
                className={SITE_SETTINGS_DATE_INPUT_CLASS}
                aria-label={`Call date ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </Card>

      <div ref={sidebarPhotosRef} className={highlightedCardClass('sidebar-photos')}>
        <SidebarPhotosSection
          adminFetch={adminFetch}
          openSections={openSections}
          setOpenSections={setOpenSections}
          styles={SITE_SETTINGS_SHARED_STYLES}
        />
      </div>

      <div ref={saveLaunchCheckRef} className={`space-y-4 ${highlightedCardClass('save-and-launch-check')}`}>
        {(saveBlockers.length > 0 || launchReadinessGaps.length > 0) && (
          <Card className={`${SITE_SETTINGS_CARD_CLASS} p-5`}>
            <div className="flex flex-col gap-4 lg:flex-row lg:justify-between">
              <div>
                <h3 className={`${SITE_SETTINGS_SECTION_TITLE_CLASS} text-base`}>
                  Save and launch check
                </h3>
                <p className={`${SITE_SETTINGS_BODY_TEXT_CLASS} mt-1`}>
                  Save blockers stop this form from persisting. Launch readiness gaps are reminders for the critical path.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className={SITE_SETTINGS_NEUTRAL_BADGE_CLASS}>
                  {saveBlockers.length} save blocker{saveBlockers.length === 1 ? '' : 's'}
                </span>
                <span className={SITE_SETTINGS_NEUTRAL_BADGE_CLASS}>
                  {launchReadinessGaps.length} launch gap{launchReadinessGaps.length === 1 ? '' : 's'}
                </span>
              </div>
            </div>

            {saveBlockers.length > 0 && (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 dark:border-red-400/20 dark:bg-red-500/5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-red-700 dark:text-red-300">
                  Fix before saving
                </p>
                <div className="mt-2 space-y-1 text-sm text-red-700 dark:text-red-200">
                  {saveBlockers.map((issue) => (
                    <p key={issue}>{issue}</p>
                  ))}
                </div>
              </div>
            )}

            {launchReadinessGaps.length > 0 && (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 dark:border-amber-400/20 dark:bg-amber-500/5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700 dark:text-amber-300">
                  Finish launch setup
                </p>
                <div className="mt-2 space-y-1 text-sm text-amber-800 dark:text-amber-100">
                  {launchReadinessGaps.map((gap) => (
                    <p key={gap}>{gap}</p>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving || saveBlockers.length > 0}
            className="bg-aqua hover:bg-aqua-dark text-charcoal gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {saveBlockers.length > 0 ? 'Fix blockers to save' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SiteSettingsTab;
